/**
 * THE CALL'S MEDIA on the runner's side (plan §12.3, owner decision 20 Q6c): the runner is the
 * caller's client. It connects to the provider's voice layer over WebRTC, exactly as the web
 * client does (`apps/web/src/components/Chat/call/webrtc-call-session.ts`): one audio track, and
 * the provider's `oai-events` data channel, on which the session sends it
 * `session.delegation.created` only. The SDP offer and answer are traded through the eval call
 * route; the media flows between this process and the provider, never through WayAI.
 *
 * - OUTBOUND, a PUMP keeps the track fed in real time, one 10 ms frame at a time: the caller's
 *   clip while one plays, silence otherwise, so the voice layer's turn detection hears speech
 *   end the way it does on a real line. Frames are sent on the wall clock, catching up after a
 *   stalled tick rather than drifting.
 * - INBOUND, the voice's audio is decoded by the WebRTC stack and measured, never stored: a
 *   VOICE-ACTIVITY detector marks when the voice starts and stops speaking, which is what the
 *   runner times its lines and the first-audio latency by.
 *
 * The WebRTC implementation is `@roamhq/wrtc` (libwebrtc for Node, a prebuilt native module of about
 * 25 MB). The operator installs it next to the CLI (`WRTC_INSTALL`); the CLI does not depend on it,
 * so no other command, user or CI install carries it, and it is loaded only when a call is placed.
 * Everything here takes an `RtcRuntime`, so the tests drive a fake peer connection.
 */

import { callDelegationId } from '@wayai/core/contracts';
import { expected } from '../expected.js';
import { OPENAI_PCM_SAMPLE_RATE, resample, type CallerClip } from './caller-audio.js';

// ---------------------------------------------------------------------------
// The WebRTC surface the runner uses — what `@roamhq/wrtc` provides, and a fake can
// ---------------------------------------------------------------------------

export interface RtcSessionDescription {
  type: 'offer' | 'answer';
  sdp: string;
}

export interface RtcTrack {
  stop(): void;
}

export interface RtcDataChannel {
  onmessage: ((event: { data: unknown }) => void) | null;
  close(): void;
}

export interface RtcPeerConnection {
  addTrack(track: RtcTrack): unknown;
  createDataChannel(label: string): RtcDataChannel;
  createOffer(): Promise<RtcSessionDescription>;
  setLocalDescription(description: RtcSessionDescription): Promise<void>;
  setRemoteDescription(description: RtcSessionDescription): Promise<void>;
  readonly localDescription: RtcSessionDescription | null;
  readonly iceGatheringState: string;
  readonly connectionState: string;
  onicegatheringstatechange: (() => void) | null;
  onconnectionstatechange: (() => void) | null;
  ontrack: ((event: { track: RtcTrack }) => void) | null;
  close(): void;
}

export interface RtcAudioFrame {
  samples: Int16Array;
  sampleRate: number;
  bitsPerSample: 16;
  channelCount: 1;
  numberOfFrames: number;
}

export interface RtcAudioSource {
  createTrack(): RtcTrack;
  onData(frame: RtcAudioFrame): void;
}

export interface RtcAudioSink {
  ondata: ((frame: { samples: Int16Array; sampleRate: number }) => void) | null;
  stop(): void;
}

export interface RtcRuntime {
  createPeerConnection(): RtcPeerConnection;
  createAudioSource(): RtcAudioSource;
  createAudioSink(track: RtcTrack): RtcAudioSink;
}

/** The package that provides WebRTC to the runner, and how an operator installs it. */
export const WRTC_PACKAGE = '@roamhq/wrtc';
export const WRTC_INSTALL = `npm install -g ${WRTC_PACKAGE}@0.10`;

/**
 * The real runtime: `@roamhq/wrtc`, loaded on demand (`importPackage` is the loader, for tests).
 * Throws an expected (not reported) error naming the install when it is not there.
 */
export async function loadWrtcRuntime(
  importPackage: () => Promise<unknown> = () => import(WRTC_PACKAGE),
): Promise<RtcRuntime> {
  let mod: Record<string, unknown>;
  try {
    mod = await importPackage() as Record<string, unknown>;
  } catch {
    // Expected, and the operator's to fix: kept off Sentry.
    throw expected(
      `Call evals need ${WRTC_PACKAGE} (WebRTC for Node), which is not installed here. `
      + `Install it next to the CLI: ${WRTC_INSTALL}`,
    );
  }
  const wrtc = ((mod.default as Record<string, unknown> | undefined) ?? mod) as {
    RTCPeerConnection: new () => RtcPeerConnection;
    nonstandard: {
      RTCAudioSource: new () => RtcAudioSource;
      RTCAudioSink: new (track: RtcTrack) => RtcAudioSink;
    };
  };
  return {
    createPeerConnection: () => new wrtc.RTCPeerConnection(),
    createAudioSource: () => new wrtc.nonstandard.RTCAudioSource(),
    createAudioSink: (track) => new wrtc.nonstandard.RTCAudioSink(track),
  };
}

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

/** One frame of audio, in milliseconds: the unit the pump sends and the detector judges. */
export const FRAME_MS = 10;

/** The pump's sample rate: OpenAI's PCM rate, so a synthesized clip goes out unresampled. */
export const PUMP_SAMPLE_RATE = OPENAI_PCM_SAMPLE_RATE;

const SAMPLES_PER_FRAME = (PUMP_SAMPLE_RATE * FRAME_MS) / 1000;

/** How often a wait re-checks the detector. */
const WAIT_POLL_MS = 20;

export interface MediaClock {
  now(): number;
}

const systemClock: MediaClock = { now: () => Date.now() };

// ---------------------------------------------------------------------------
// Voice activity — when the voice speaks, from its decoded audio
// ---------------------------------------------------------------------------

export interface VoiceActivityOptions {
  /** RMS level (of 32,767) above which a frame is speech. */
  threshold?: number;
  /** Speech for this long starts an utterance. */
  onsetMs?: number;
  /** Silence for this long ends one. */
  releaseMs?: number;
}

/**
 * A plain energy detector over the voice's decoded audio: the provider's own audio carries no
 * background noise to speak of, and the runner hears no echo of itself (there is no room, only a
 * track), so a level threshold with a short onset and a release is enough to time speech by.
 */
export class VoiceActivity {
  private readonly threshold: number;
  private readonly onsetMs: number;
  private readonly releaseMs: number;
  private loudMs = 0;
  private quietMs = 0;
  private loudSince: number | null = null;
  private isSpeaking = false;
  /** When each utterance began and ended, on the runner's clock. */
  readonly onsets: number[] = [];
  readonly offsets: number[] = [];

  constructor(private readonly clock: MediaClock = systemClock, options: VoiceActivityOptions = {}) {
    this.threshold = options.threshold ?? 500;
    this.onsetMs = options.onsetMs ?? 30;
    this.releaseMs = options.releaseMs ?? 450;
  }

  /** Whether the voice is speaking now. */
  get speaking(): boolean {
    return this.isSpeaking;
  }

  /** Judge decoded audio as it arrives (the sink delivers it live, about 10 ms at a time). */
  push(samples: Int16Array, sampleRate: number): void {
    const frame = Math.max(1, Math.round((sampleRate * FRAME_MS) / 1000));
    const at = this.clock.now();
    const frames = Math.ceil(samples.length / frame);
    for (let f = 0; f < frames; f++) {
      let sum = 0;
      const start = f * frame;
      const end = Math.min(samples.length, start + frame);
      for (let i = start; i < end; i++) sum += samples[i] * samples[i];
      const loud = Math.sqrt(sum / Math.max(1, end - start)) >= this.threshold;
      // Each frame's own time: the pushed audio ends now.
      const frameAt = at - (frames - 1 - f) * FRAME_MS;
      this.judge(loud, frameAt);
    }
  }

  private judge(loud: boolean, at: number): void {
    if (loud) {
      this.quietMs = 0;
      if (this.loudSince === null) this.loudSince = at;
      this.loudMs += FRAME_MS;
      if (!this.isSpeaking && this.loudMs >= this.onsetMs) {
        this.isSpeaking = true;
        this.onsets.push(this.loudSince);
      }
      return;
    }
    this.loudMs = 0;
    this.loudSince = null;
    if (!this.isSpeaking) return;
    this.quietMs += FRAME_MS;
    if (this.quietMs >= this.releaseMs) {
      this.isSpeaking = false;
      this.offsets.push(at - this.quietMs + FRAME_MS);
    }
  }

  /** The first onset after `afterMs`, or null. */
  onsetAfter(afterMs: number): number | null {
    return this.onsets.find((t) => t > afterMs) ?? null;
  }

  /** When the voice last stopped speaking; null while it speaks or before it has. */
  quietSince(): number | null {
    return this.isSpeaking ? null : (this.offsets[this.offsets.length - 1] ?? null);
  }
}

// ---------------------------------------------------------------------------
// The outbound pump — the caller's audio, in real time
// ---------------------------------------------------------------------------

export interface PlaybackSpan {
  /** When the clip's first frame went out, on the runner's clock. */
  startedAtMs: number;
  /** When its last frame went out: the caller's end of speech. */
  endedAtMs: number;
  /** True when `abort` cut the clip short. */
  cut: boolean;
}

interface QueuedClip {
  samples: Int16Array;
  offset: number;
  startedAtMs: number | null;
  abort: () => boolean;
  resolve: (span: PlaybackSpan) => void;
}

export class AudioPump {
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;
  private sent = 0;
  private readonly queue: QueuedClip[] = [];
  private readonly silence = new Int16Array(SAMPLES_PER_FRAME);

  constructor(private readonly source: RtcAudioSource, private readonly clock: MediaClock = systemClock) {}

  start(): void {
    if (this.timer) return;
    this.startedAt = this.clock.now();
    this.sent = 0;
    this.timer = setInterval(() => this.tick(), FRAME_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Queue a clip after any still playing; resolves once its last frame is sent, or once `abort`,
   * asked before every frame, answers true — the rest of the clip is then dropped.
   */
  play(clip: CallerClip, abort: () => boolean = () => false): Promise<PlaybackSpan> {
    return new Promise((resolve) => {
      this.queue.push({ samples: resample(clip, PUMP_SAMPLE_RATE), offset: 0, startedAtMs: null, abort, resolve });
    });
  }

  /** Send every frame the wall clock says is due. */
  tick(): void {
    const due = Math.floor((this.clock.now() - this.startedAt) / FRAME_MS) - this.sent;
    for (let i = 0; i < due; i++) this.sendFrame();
  }

  private sendFrame(): void {
    const now = this.clock.now();
    let clip: QueuedClip | undefined = this.queue[0];
    if (clip?.abort()) {
      this.queue.shift();
      clip.resolve({ startedAtMs: clip.startedAtMs ?? now, endedAtMs: now, cut: true });
      clip = undefined;
    }
    let samples = this.silence;
    if (clip) {
      clip.startedAtMs ??= now;
      samples = new Int16Array(SAMPLES_PER_FRAME);
      samples.set(clip.samples.subarray(clip.offset, clip.offset + SAMPLES_PER_FRAME));
      clip.offset += SAMPLES_PER_FRAME;
      if (clip.offset >= clip.samples.length) {
        this.queue.shift();
        clip.resolve({ startedAtMs: clip.startedAtMs, endedAtMs: now + FRAME_MS, cut: false });
      }
    }
    this.source.onData({
      samples,
      sampleRate: PUMP_SAMPLE_RATE,
      bitsPerSample: 16,
      channelCount: 1,
      numberOfFrames: SAMPLES_PER_FRAME,
    });
    this.sent += 1;
  }
}

// ---------------------------------------------------------------------------
// The call's media
// ---------------------------------------------------------------------------

/** The provider's events channel, opened before the offer as the web client opens it. */
export const PROVIDER_EVENTS_CHANNEL = 'oai-events';

/** The offer waits for ICE gathering this long at most: the server trades one offer, no trickle. */
export const ICE_GATHERING_MAX_MS = 2_000;

/** How long the media connection may take to come up once the answer is applied. */
export const MEDIA_CONNECT_TIMEOUT_MS = 15_000;

/** What the runner needs of the call's media; `CallMedia` is it, and the runner's tests fake it. */
export interface CallMediaPort {
  /** The SDP offer for the eval call route. */
  offer(): Promise<string>;
  /** Apply the provider's answer and wait for the media to connect; starts the pump. */
  connect(sdpAnswer: string): Promise<void>;
  /** Say a clip in real time; `abort`, asked before every 10 ms frame, cuts it short. */
  play(clip: CallerClip, abort?: () => boolean): Promise<PlaybackSpan>;
  /**
   * The voice's first onset after `afterMs`, waited for up to `timeoutMs`; null if none. `abort`
   * is asked at every check and ends the wait early when it answers true.
   */
  waitForVoiceOnset(afterMs: number, timeoutMs: number, abort?: () => boolean): Promise<number | null>;
  /**
   * Wait until the voice has spoken since `sinceMs` and has then been quiet `quietMs`, up to
   * `timeoutMs`, or until `abort` answers true. True when the voice is done; false otherwise.
   */
  waitForVoiceDone(sinceMs: number, quietMs: number, timeoutMs: number, abort?: () => boolean): Promise<boolean>;
  /** The distinct delegations the provider reported on the events channel so far. */
  delegationsSeen(): number;
  /** Whether the media is still connected: a call the server ended closes it. */
  connected(): boolean;
  close(): void;
}

export class CallMedia implements CallMediaPort {
  private pc: RtcPeerConnection | null = null;
  private source: RtcAudioSource | null = null;
  private track: RtcTrack | null = null;
  private sink: RtcAudioSink | null = null;
  private events: RtcDataChannel | null = null;
  private pump: AudioPump | null = null;
  private readonly delegations = new Set<string>();
  readonly voice: VoiceActivity;

  constructor(private readonly runtime: RtcRuntime, private readonly clock: MediaClock = systemClock, voice?: VoiceActivityOptions) {
    this.voice = new VoiceActivity(clock, voice);
  }

  async offer(): Promise<string> {
    const pc = this.runtime.createPeerConnection();
    this.pc = pc;
    this.source = this.runtime.createAudioSource();
    this.track = this.source.createTrack();
    pc.addTrack(this.track);
    pc.ontrack = (event) => {
      this.sink?.stop();
      this.sink = this.runtime.createAudioSink(event.track);
      this.sink.ondata = (frame) => this.voice.push(frame.samples, frame.sampleRate);
    };
    this.events = pc.createDataChannel(PROVIDER_EVENTS_CHANNEL);
    this.events.onmessage = (event) => this.onProviderEvent(event.data);

    await pc.setLocalDescription(await pc.createOffer());
    await waitFor(() => pc.iceGatheringState === 'complete', ICE_GATHERING_MAX_MS, this.clock);
    const sdp = pc.localDescription?.sdp;
    if (!sdp) throw new Error('WebRTC produced no SDP offer.');
    return sdp;
  }

  async connect(sdpAnswer: string): Promise<void> {
    const pc = this.pc;
    if (!pc || !this.source) throw new Error('The call has no offer to answer.');
    await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
    const connected = await waitFor(
      () => pc.connectionState === 'connected' || pc.connectionState === 'failed' || pc.connectionState === 'closed',
      MEDIA_CONNECT_TIMEOUT_MS,
      this.clock,
    );
    if (!connected || pc.connectionState !== 'connected') {
      throw new Error(`The call's media did not connect (${pc.connectionState}).`);
    }
    this.pump = new AudioPump(this.source, this.clock);
    this.pump.start();
  }

  play(clip: CallerClip, abort?: () => boolean): Promise<PlaybackSpan> {
    if (!this.pump) return Promise.reject(new Error('The call\'s media is not connected.'));
    return this.pump.play(clip, abort);
  }

  async waitForVoiceOnset(afterMs: number, timeoutMs: number, abort: () => boolean = () => false): Promise<number | null> {
    await waitFor(() => this.voice.onsetAfter(afterMs) !== null || abort(), timeoutMs, this.clock);
    return this.voice.onsetAfter(afterMs);
  }

  async waitForVoiceDone(sinceMs: number, quietMs: number, timeoutMs: number, abort: () => boolean = () => false): Promise<boolean> {
    const done = () => {
      if (this.voice.onsetAfter(sinceMs - 1) === null) return false;
      const quiet = this.voice.quietSince();
      return quiet !== null && quiet > sinceMs && this.clock.now() - quiet >= quietMs;
    };
    await waitFor(() => done() || abort(), timeoutMs, this.clock);
    return done();
  }

  delegationsSeen(): number {
    return this.delegations.size;
  }

  connected(): boolean {
    return this.pc?.connectionState === 'connected';
  }

  close(): void {
    this.pump?.stop();
    this.sink?.stop();
    this.track?.stop();
    this.events?.close();
    this.pc?.close();
  }

  private onProviderEvent(data: unknown): void {
    if (typeof data !== 'string') return;
    try {
      const event = JSON.parse(data) as { type?: unknown; delegation?: { id?: unknown } };
      if (event.type !== 'session.delegation.created') return;
      // Only an id the server would accept counts: it is what the live operations count reads.
      const id = callDelegationId.safeParse(event.delegation?.id);
      if (id.success) this.delegations.add(id.data);
    } catch {
      // Not an event this runner reads.
    }
  }
}

/** Resolve true once `ready()` holds, or false after `timeoutMs`. */
export function waitFor(ready: () => boolean, timeoutMs: number, clock: MediaClock = systemClock): Promise<boolean> {
  if (ready()) return Promise.resolve(true);
  const deadline = clock.now() + timeoutMs;
  return new Promise((resolve) => {
    const timer = setInterval(() => {
      if (ready()) {
        clearInterval(timer);
        resolve(true);
      } else if (clock.now() >= deadline) {
        clearInterval(timer);
        resolve(false);
      }
    }, WAIT_POLL_MS);
  });
}
